/**
 * The one component in this app that writes a key.
 *
 * `provider` is a PROP and is never derived. That single decision is what this
 * file is for: while the draft's owner was inferred from whichever radio was
 * checked, a key typed under Anthropic and a click on an OpenAI row were one
 * render apart from `setKey('openai', anthropicSecret)` — and a key sent to the
 * wrong host has already left the page by the time anything rejects it. The old
 * sheet guarded that with "if the provider changed, clear the draft"; here the
 * question cannot be asked, because the draft and the destination are the same
 * prop. A guard can be forgotten in a refactor. A prop cannot.
 *
 * Three things it is careful about, unchanged from the sheet it was lifted out of:
 *
 *  - It never populates the key field from storage. Empty means "leave what is
 *    stored alone"; removing is a separate, confirmed action. A masked value that
 *    is really a live secret in an input is the shape that gets pasted somewhere.
 *  - It warns about a pasted key's shape and NEVER refuses to save one.
 *    `'sk-ant-…'.startsWith('sk-')` is true, so the obvious per-provider prefix
 *    test accepts exactly the paste that matters — and locking a paying user out
 *    of their own key is worse than a rejected request whose error they can read.
 *  - It says where the key goes, in the provider's own hostname, once.
 *
 * The value never leaves. The sheet's Save does not receive a key and could not
 * misfile one: it calls `commit()` through `register`, and gets back only what
 * happened — which provider, whether a key was written or replaced, and the host
 * of a custom endpoint, which is an address rather than a secret.
 */

import { useEffect, useRef, useState } from 'react'
import { useStore, type KeyAsk } from '../core/store'
import { track } from '../core/analytics'
import { checkKey, keyCheckable, keyRejectedLine, type KeyCheck } from '../engine/keyCheck'
import { missingKeyLine } from './pressName'
import {
  JUDGE_ENDPOINT,
  PROVIDERS,
  baseUrlProblem,
  consoleHost,
  hostOfBase,
  houseModelId,
  judgeHost,
  keyHouseGuess,
  keyShapeWarning,
  type ProviderEntry,
  type ProviderId,
} from '../core/models'

/**
 * The card's one sentence about why the sheet opened on it (`keyAsk`).
 *
 * A rejected key says the provider turned it away (D193). A missing one says what
 * needed it and on which model, so a reader sent here by a routed source check
 * reads that the check is what wants an OpenAI key, not the reasoning they
 * already pay for — "The source check runs on <model> — it needs an OpenAI key."
 * The endpoint row is refused for its address, never its key (a model on this
 * machine needs none), so that is what it asks for.
 */
export function keyAskLine(ask: KeyAsk, house: ProviderEntry): string {
  if (ask.why === 'rejected') {
    return `${house.name} rejected this key on the last pass. Paste a working one; anything you had typed is back in its box.`
  }
  // One sentence with the notes that say it before the press (`missingKeyLine`).
  return missingKeyLine(house, ask.need)
}

/** Enough to recognise a key by, and not enough to use. */
const maskKey = (k: string): string =>
  k.length <= 12 ? `${k.slice(0, 3)}…` : `${k.slice(0, 7)}…${k.slice(-4)}`

/**
 * What a card actually wrote, for the sheet's one toast.
 *
 * Deliberately not the key, and deliberately not "success": the sheet composes a
 * different sentence for a first connection than for a replacement, and it needs
 * the endpoint's host to name where a custom address now points.
 */
export interface KeyCardResult {
  readonly provider: ProviderId
  readonly key: 'none' | 'saved' | 'replaced'
  /** The host a custom endpoint was pointed at, when this card wrote one. */
  readonly endpoint: string | null
  /**
   * What the provider said about the key just written, on a free call made by
   * this card — the card holds the key, so the check can only go to the host
   * this card names (`engine/keyCheck.ts`). Null when nothing was written or the
   * house has no free call to ask. The sheet awaits it to choose its sentence;
   * the key is written before it starts and stays written whatever it says.
   */
  readonly check: Promise<KeyCheck> | null
}

export function ProviderKeyCard({
  provider,
  activeModelName,
  onReady,
  register,
  onSubmit,
  onSwitchHouse,
}: {
  provider: ProviderId
  /** The model this provider currently runs, when it owns the selection. Used in
   *  exactly one sentence: what stops working if the key is removed. */
  activeModelName?: string
  /** Whether this provider could run a pass after Save — a key typed or stored,
   *  or, for a custom endpoint, an address that parses. The sheet's Save is
   *  disabled on it, so it is reported rather than recomputed there. */
  /** `null` RETRACTS the report — see the unmount effect below. */
  onReady?: (provider: ProviderId, ready: boolean | null) => void
  /** Publishes `commit` to the sheet, which calls it from its own Save. Passing
   *  the function rather than the draft is the point — see the file header. */
  register?: (provider: ProviderId, commit: (() => KeyCardResult) | null) => void
  /** Enter in the key field. The sheet decides what saving means; this only says
   *  the user asked for it. */
  onSubmit?: () => void
  /**
   * Move the selection to a model of another house — offered when the key typed
   * here looks like that house's (D335). The sheet owns the selection; this card
   * only says which house the reader asked for. The draft is NOT handed over: it
   * is cleared here, and the reader pastes it again in that house's field, so a
   * typed key still never crosses houses in code (see the file header).
   */
  onSwitchHouse?: (p: ProviderId) => void
}) {
  const mode = useStore((s) => s.setupOpen)
  const keys = useStore((s) => s.keys)
  const setKey = useStore((s) => s.setKey)
  const storedBase = useStore((s) => s.customBase)
  const storedCustomModel = useStore((s) => s.customModel)
  const storedDialect = useStore((s) => s.customDialect)
  const setCustom = useStore((s) => s.setCustom)
  const toast = useStore((s) => s.toast)

  const entry = PROVIDERS[provider]
  const isCustom = entry.custom === true
  const stored = keys[provider]

  const [draft, setDraft] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(false)
  // Remove asks first, and the question replaces the button that asked it — so
  // focus fell to the sheet's body under the key that pressed it. It goes to
  // Keep, the answer that changes nothing, and back to Remove on Keep.
  const removeBtn = useRef<HTMLButtonElement | null>(null)
  const keepBtn = useRef<HTMLButtonElement | null>(null)
  const wasConfirming = useRef(confirmRemove)
  useEffect(() => {
    if (confirmRemove === wasConfirming.current) return
    wasConfirming.current = confirmRemove
    const here = document.activeElement
    if (here && here !== document.body && here.isConnected) return
    ;(confirmRemove ? keepBtn : removeBtn).current?.focus()
  }, [confirmRemove])
  // What the provider said on Save's check, while this card is up. 'checking'
  // between the press and the answer.
  const [verdict, setVerdict] = useState<KeyCheck | 'checking' | null>(null)
  // Why a pass just sent the reader here (`askForKey`) — this house's key was
  // rejected, or there is none — when it is this card: the card says so and takes
  // the caret, so the reader lands on the field that needs them. On the endpoint
  // row a missing one points at the address, the thing without which there is
  // nothing to call.
  const ask = useStore((s) => (s.keyAsk?.provider === provider ? s.keyAsk : null))
  const asked = ask !== null
  const askedAddress = ask?.why === 'missing' && isCustom
  const field = useRef<HTMLInputElement | null>(null)
  const addressField = useRef<HTMLInputElement | null>(null)
  const card = useRef<HTMLDivElement | null>(null)
  // The endpoint's two strings ARE prefilled, unlike the key: an address is not a
  // secret, and a URL field that came up empty every time would mean retyping it
  // to change the model name beside it.
  const [base, setBase] = useState(storedBase)
  const [customModel, setCustomModel] = useState(storedCustomModel)
  // The API the address speaks rides with the address, prefilled for the same
  // reason: it is a fact about the endpoint rather than a secret, and a choice
  // that came up reset would switch a working local row back to an API its
  // server does not speak on the next unrelated save.
  const [dialect, setDialect] = useState(storedDialect)

  /**
   * Reset on every OPEN, and on any change of `provider`. A correctness fix
   * rather than tidiness, in both directions.
   *
   * The sheet is mounted for the life of the app and only returns null while
   * closed, so a secret typed and then abandoned with Escape — which closes
   * through the store, not through the sheet's own `close()` — would still be
   * sitting in the field the next time it opened. And on the splash the card
   * follows the picked model, so a provider change with a live draft is the one
   * moment a key could cross houses; the prop makes the write safe, this makes
   * the field honest about which house it now belongs to.
   */
  useEffect(() => {
    if (!mode) return
    const st = useStore.getState()
    setDraft('')
    setConfirmRemove(false)
    setVerdict(null)
    setBase(st.customBase)
    setCustomModel(st.customModel)
    setDialect(st.customDialect)
  }, [mode, provider])

  // The caret goes where the refusal points: the field whose key a pass just saw
  // rejected or found missing, or the one whose Save the provider just turned
  // away. A frame late, so the sheet's own focus trap has seated first. The whole
  // card is scrolled into view rather than the field alone, because the sentence
  // saying why sits under the field, and on a phone the second provider's card
  // starts below the fold.
  const rejected = verdict !== null && verdict !== 'checking' && verdict.kind === 'rejected'
  useEffect(() => {
    if (!mode || !(asked || rejected)) return
    const id = requestAnimationFrame(() => {
      const target = askedAddress && !rejected ? addressField.current : field.current
      target?.focus({ preventScroll: true })
      card.current?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(id)
  }, [mode, asked, askedAddress, rejected])

  const typed = draft.trim()
  const warning = typed ? keyShapeWarning(provider, typed) : null
  // The house the pasted key looks like, when that is another one the picker can
  // run — the likeliest newcomer mistake is the OpenAI or Gemini key they already
  // have, pasted into the Anthropic card the splash opens on. Offered, never
  // done: Save stays lit, because a guess from a prefix refuses nothing.
  const guess = typed ? keyHouseGuess(typed) : null
  const switchTo =
    onSwitchHouse && guess && guess !== provider && houseModelId(guess) !== null ? guess : null
  const switchHouse = (p: ProviderId) => {
    setDraft('')
    onSwitchHouse?.(p)
    // The caret follows to the field the reader is about to paste into — on the
    // splash a fresh card mounts for that house, in Settings it is the card below.
    // Found by its label, which is the registry's and unique per house.
    const label = PROVIDERS[p].keyLabel
    requestAnimationFrame(() =>
      document.querySelector<HTMLInputElement>(`input[aria-label="${label}"]`)?.focus(),
    )
  }
  const baseProblem = isCustom && base.trim() ? baseUrlProblem(base) : null
  // A custom endpoint is ready when it has an address — a model on this machine
  // needs no credential. Everything else needs a key.
  const ready = isCustom ? !!base.trim() && !baseProblem : !!(typed || stored)
  // The proxy suggestion that used to close every provider's note lives on the
  // endpoint card now, which exists only where that row is offered — so it needs
  // no `SELECTABLE_MODELS` test of its own to avoid offering a hidden row.

  useEffect(() => {
    onReady?.(provider, ready)
  }, [onReady, provider, ready])

  // Readiness is retracted when the card goes away. The sections mount one panel
  // at a time, so leaving the Providers tab unmounts this — and a stale "ready"
  // would leave the sheet's Save lit for a draft that no longer exists anywhere.
  // A draft does not survive leaving the card, exactly as it does not survive
  // Escape; see the reset above for why that is deliberate.
  // `null`, not `false`. `false` means "this card is up and has no key in it";
  // `null` means "there is no card, ask storage instead". Reported as `false` it
  // satisfied the `??` in the sheet's `willHaveKey` and pinned Save disabled on a
  // workspace whose key was sitting on disk the whole time.
  useEffect(() => () => onReady?.(provider, null), [onReady, provider])

  useEffect(() => {
    if (!register) return
    // No dependency array on purpose: the closure has to see THIS render's draft,
    // and a stale one would write the key before last keystroke. Re-registering
    // every render is one Map.set, which is cheaper than getting this subtly wrong.
    const commit = (): KeyCardResult => {
      const hadKey = !!stored
      let wrote: KeyCardResult['key'] = 'none'
      let check: Promise<KeyCheck> | null = null
      if (typed) {
        setKey(provider, typed)
        // Only when a key was actually entered. An empty field means "leave storage
        // alone", and counting that would count somebody who saved a routing change
        // as somebody who connected. `provider` and nothing else — never the key,
        // never the endpoint, which on the custom row is an address the user typed.
        track('key_saved', { provider })
        wrote = hadKey ? 'replaced' : 'saved'
        setDraft('')
        // Asked of this provider's own host, with the key just written, for free.
        // Written FIRST and asked after: the check informs a sentence and never
        // decides whether the key is kept — the rule above about refusing none.
        if (keyCheckable(provider)) {
          setVerdict('checking')
          check = checkKey(provider, typed)
          void check.then(setVerdict)
        }
      }
      let endpoint: string | null = null
      if (isCustom && base.trim() && !baseProblem) {
        setCustom(base, customModel, dialect)
        endpoint = hostOfBase(base)
      }
      return { provider, key: wrote, endpoint, check }
    }
    register(provider, commit)
    return () => register(provider, null)
  })

  const checkingLine = `Asking ${entry.name} whether it takes this key — a free call, nothing is billed.`

  const remove = () => {
    setKey(provider, null)
    setConfirmRemove(false)
    toast(
      activeModelName
        ? `Key removed. ${activeModelName} cannot be called until you paste another.`
        : `${entry.name} key removed.`,
      'info',
    )
  }

  return (
    <div className="field" ref={card}>
      {isCustom && (
        <>
          <span className="eyebrow">Endpoint</span>
          {/* No Ollama port in the example any more. The adapter calls the
              Responses API, which Ollama and LM Studio do not serve, so an
              example pointing at one was an instruction that could not work —
              the row's note in the registry says which API it needs. */}
          <input
            className="input"
            value={base}
            placeholder="https://your-proxy.example.com/v1"
            spellCheck={false}
            onChange={(e) => setBase(e.target.value)}
            aria-label="Endpoint base URL"
            ref={addressField}
          />
          {ask && askedAddress && !base.trim() && (
            <p className="gnote gnote--warn" role="status">
              {keyAskLine(ask, entry)}
            </p>
          )}
          {baseProblem && <p className="gnote gnote--warn">{baseProblem}</p>}
          <input
            className="input"
            value={customModel}
            placeholder="Model name your endpoint expects"
            spellCheck={false}
            onChange={(e) => setCustomModel(e.target.value)}
            aria-label="Model name"
          />
          {/* A radiogroup of two, in the shape the Appearance section uses. It sits
              with the address rather than with the key because it is a fact about
              the address: one host speaks one of these, and a model on this
              machine speaks only the second. */}
          <div
            role="radiogroup"
            aria-label="API the endpoint speaks"
            style={{ display: 'flex', gap: 4 }}
          >
            {(['responses', 'chat'] as const).map((d) => (
              <button
                key={d}
                className="btchip"
                role="radio"
                aria-checked={dialect === d}
                data-on={dialect === d || undefined}
                onClick={() => setDialect(d)}
              >
                {d === 'responses' ? 'Responses API' : 'Chat Completions'}
              </button>
            ))}
          </div>
          <p className="gnote gnote--quiet">
            Responses API for OpenAI itself and a proxy or gateway built against it; Chat
            Completions for Ollama, LM Studio and most local servers.
          </p>
          <p className="gnote gnote--quiet">
            Anything that speaks one of the two: a proxy you run, which keeps your key off this
            browser entirely; a gateway your work already provides; or a model on this machine
            through Ollama or LM Studio, which needs no key at all. The model name is sent as typed,
            and every node on the canvas is stamped with it.
          </p>
          <p className="gnote gnote--warn">
            A Claude or ChatGPT subscription can’t run this: those plans cover their own apps only,
            so whatever you point this at needs its own API access.
          </p>
        </>
      )}

      <span className="eyebrow">{entry.keyLabel}</span>
      <input
        className="input"
        type="password"
        autoComplete="off"
        spellCheck={false}
        placeholder={entry.keyHint}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && ready) onSubmit?.()
        }}
        aria-label={entry.keyLabel}
        ref={field}
      />
      {warning && (
        <p className="gnote gnote--warn">
          {warning}
          {switchTo && (
            <>
              {' '}
              <button type="button" className="linkish" onClick={() => switchHouse(switchTo)}>
                Use {/^[AEIOU]/.test(PROVIDERS[switchTo].name) ? 'an' : 'a'}{' '}
                {PROVIDERS[switchTo].name} model instead
              </button>{' '}
              and paste it again there.
            </>
          )}
        </p>
      )}
      {/* What the provider said, in its own words, and nothing stronger: a check
          that could not run says nothing here (the sheet's toast says the key was
          saved and meets its provider on the first pass). */}
      {/* The check's one live region, mounted before it has anything to say: a
          status paragraph that arrived already holding its sentence was one a
          screen reader never saw change, so Save went quiet for the whole check.
          Out of flow (`.sr-only` is absolute), so it takes no gap in the field.
          The sentence below is the same words for the eye. */}
      <span className="sr-only" role="status">
        {verdict === 'checking' ? checkingLine : ''}
      </span>
      {verdict === 'checking' ? (
        <p className="gnote gnote--quiet">{checkingLine}</p>
      ) : rejected ? (
        <p className="gnote gnote--warn" role="alert">
          {keyRejectedLine(entry.name, verdict.message)}
        </p>
      ) : (
        ask &&
        !askedAddress &&
        !typed && (
          <p className="gnote gnote--warn" role="status">
            {keyAskLine(ask, entry)}
          </p>
        )
      )}
      {stored && !typed ? (
        confirmRemove ? (
          <div className="docs-confirm">
            <span>
              Remove it? A pass on {entry.name} will stop until you paste another. Nothing on the
              canvas changes.
            </span>
            <button
              className="btn btn--bad"
              aria-label={`Remove the ${entry.name} key`}
              onClick={remove}
            >
              Remove
            </button>
            <button
              ref={keepBtn}
              className="btn"
              aria-label={`Keep the ${entry.name} key`}
              onClick={() => setConfirmRemove(false)}
            >
              Keep
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="srcs-meta">{maskKey(stored)} · stored in this browser</span>
            <button
              ref={removeBtn}
              className="btn"
              style={{ color: 'var(--text-3)' }}
              aria-label={`Remove the ${entry.name} key`}
              onClick={() => setConfirmRemove(true)}
            >
              Remove
            </button>
          </div>
        )
      ) : (
        !stored &&
        !isCustom && (
          // A link to the provider's own key page, not a host to retype: the
          // registry has always held the deep link, and finding the keys page on
          // someone else's console is the commonest thing between a newcomer and
          // a first result (D335). The host stays the visible text, so the reader
          // still sees where they are going. Static registry data; nothing about
          // the key moves. `.linkish`, the app's inline press: the stylesheet has
          // no bare `a` rule, so a plain anchor wore the browser's blue, then its
          // visited purple, at text height on touch.
          <p className="gnote gnote--quiet">
            Get one at{' '}
            <a
              className="linkish num"
              href={entry.consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Opens the provider’s key page in a new tab"
            >
              {consoleHost(entry)}
            </a>
          </p>
        )
      )}

      {/* Where the key goes, once, in one sentence — by the provider's name from
          the registry and its own hostname. The longer account (no server in
          between, nothing else reads it, one provider's key never goes to another)
          is in docs/guide.md, under "Starting". The judge's card exists only on a
          build that named an endpoint, and the key goes THERE — a proxy build must
          not print the API's host under a key that leaves for somewhere else
          (D107). A custom address and a judge endpoint are somebody else's
          servers, so those two say who is being trusted; that is the caveat, next
          to the field it is about. */}
      <p className="gnote">
        {isCustom ? (
          <>
            Your key, if any, stays in this browser and goes only to{' '}
            <span className="num">{hostOfBase(base)}</span>. Whoever runs that address sees your
            prompts.
          </>
        ) : entry.judge ? (
          <>
            Your key stays in this browser and goes only to{' '}
            <span className="num">{judgeHost(JUDGE_ENDPOINT)}</span>, the address this build names
            for the judge. Whoever runs it sees the key and every question sent through it.
          </>
        ) : (
          <>
            Your key stays in this browser and goes only to {entry.name} (
            <span className="num">{entry.host}</span>).
          </>
        )}
      </p>
    </div>
  )
}
